import { useCallback, useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import Form, { FieldOverrides } from '@/components/Form';
import { UserFragment } from '@/graphql/fragments/generated/User';

import {
  getInitialPraiseValues,
  getPraiseFormFields,
  getPraiseSectionLayout,
  GivePraiseFormValues,
  validatePraiseForm,
} from './config';
import { GivePraiseFormProps } from './types';

const OVERRIDES: FieldOverrides = {
  Section: {
    spacing: 'space-y-32',
    Header: { className: 'mb-16' },
    Name: { variant: 'heading-xs', className: 'ms-8' },
  },
};

const GivePraiseForm: React.FC<GivePraiseFormProps> = (props) => {
  const { setFormikSubmit, onSubmit, praise, onChange, users, initialFormValues } = props;

  const { t } = useTranslation();

  const { initialValues, fields, sectionLayout } = useMemo(
    () => ({
      fields: getPraiseFormFields({ t }),
      initialValues: { ...getInitialPraiseValues({ praise, users }), ...initialFormValues },
      sectionLayout: getPraiseSectionLayout(t),
    }),
    [initialFormValues, praise, t, users],
  );

  const onSubmitForm = useCallback(
    (values: GivePraiseFormValues) => {
      const { receiverUsers, medias, ...restValues } = values;

      const receiverUserIds = receiverUsers.map((user: UserFragment) => user.id);
      const mediasWithoutId = medias?.map(({ mediaId, ...rest }) => rest);

      onSubmit({
        ...restValues,
        receiverUserIds,
        attachments: mediasWithoutId,
      });
    },
    [onSubmit],
  );

  return (
    <Form<GivePraiseFormValues>
      fields={fields}
      formName="GivePraise"
      initialValues={initialValues}
      overrides={OVERRIDES}
      sectionLayout={sectionLayout}
      setSubmitRef={setFormikSubmit}
      validate={validatePraiseForm}
      validateOnMount={false}
      onChange={onChange}
      onSubmit={onSubmitForm}
    />
  );
};

export default GivePraiseForm;
